import React, { useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const PostgreSQLConnectionForm = ({ details, onChange, errors = {}, readOnly = false }) => {
  const [showPassword, setShowPassword] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange({
      ...details,
      [name]: value
    });
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
        <div className="sm:col-span-4">
          <label htmlFor="host" className="block text-sm font-medium text-secondary-700">
            Host
          </label>
          <input
            type="text"
            name="host"
            id="host"
            value={details.host || ''}
            onChange={handleChange}
            readOnly={readOnly}
            className={`mt-1 block w-full shadow-sm sm:text-sm border-secondary-300 rounded-md ${
              readOnly 
                ? 'bg-gray-50 text-gray-900 cursor-default focus:ring-0 focus:border-secondary-300' 
                : 'focus:ring-primary-500 focus:border-primary-500'
            } ${errors.host ? 'border-danger-300' : ''}`}
            placeholder="localhost or db.example.com"
          />
          {errors.host && (
            <p className="mt-2 text-sm text-danger-600">{errors.host}</p>
          )}
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="port" className="block text-sm font-medium text-secondary-700">
            Port
          </label>
          <input
            type="text"
            name="port"
            id="port"
            value={details.port || '5432'}
            onChange={handleChange}
            readOnly={readOnly}
            className={`mt-1 block w-full shadow-sm sm:text-sm border-secondary-300 rounded-md ${
              readOnly 
                ? 'bg-gray-50 text-gray-900 cursor-default focus:ring-0 focus:border-secondary-300' 
                : 'focus:ring-primary-500 focus:border-primary-500'
            } ${errors.port ? 'border-danger-300' : ''}`}
            placeholder={readOnly ? '' : "5432"}
          />
          {errors.port && (
            <p className="mt-2 text-sm text-danger-600">{errors.port}</p>
          )}
        </div>

        <div className="sm:col-span-3">
          <label htmlFor="database" className="block text-sm font-medium text-secondary-700">
            Database
          </label>
          <input
            type="text"
            name="database"
            id="database"
            value={details.database || ''}
            onChange={handleChange}
            readOnly={readOnly}
            className={`mt-1 block w-full shadow-sm sm:text-sm border-secondary-300 rounded-md ${
              readOnly 
                ? 'bg-gray-50 text-gray-900 cursor-default focus:ring-0 focus:border-secondary-300' 
                : 'focus:ring-primary-500 focus:border-primary-500'
            } ${errors.database ? 'border-danger-300' : ''}`}
            placeholder={readOnly ? '' : "postgres"}
          />
          {errors.database && (
            <p className="mt-2 text-sm text-danger-600">{errors.database}</p>
          )}
        </div>

        <div className="sm:col-span-3">
          <label htmlFor="schema" className="block text-sm font-medium text-secondary-700">
            Schema
          </label>
          <input
            type="text"
            name="schema"
            id="schema"
            value={details.schema || ''}
            onChange={handleChange}
            readOnly={readOnly}
            className={`mt-1 block w-full shadow-sm sm:text-sm border-secondary-300 rounded-md ${
              readOnly 
                ? 'bg-gray-50 text-gray-900 cursor-default focus:ring-0 focus:border-secondary-300' 
                : 'focus:ring-primary-500 focus:border-primary-500'
            } ${errors.schema ? 'border-danger-300' : ''}`}
            placeholder={readOnly ? '' : "public"}
          />
          {errors.schema && (
            <p className="mt-2 text-sm text-danger-600">{errors.schema}</p>
          )}
          <p className="mt-2 text-xs text-secondary-500">
            Optional. Defaults to public if not specified
          </p>
        </div>

        <div className="sm:col-span-3">
          <label htmlFor="username" className="block text-sm font-medium text-secondary-700">
            Username
          </label>
          <input
            type="text"
            name="username"
            id="username"
            value={details.username || ''}
            onChange={handleChange}
            readOnly={readOnly}
            className={`mt-1 block w-full shadow-sm sm:text-sm border-secondary-300 rounded-md ${
              readOnly 
                ? 'bg-gray-50 text-gray-900 cursor-default focus:ring-0 focus:border-secondary-300' 
                : 'focus:ring-primary-500 focus:border-primary-500'
            } ${errors.username ? 'border-danger-300' : ''}`}
            placeholder={readOnly ? '' : "postgres"}
          />
          {errors.username && (
            <p className="mt-2 text-sm text-danger-600">{errors.username}</p>
          )}
        </div>

        <div className="sm:col-span-3">
          <label htmlFor="password" className="block text-sm font-medium text-secondary-700">
            Password
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              id="password"
              value={details.password || ''}
              onChange={handleChange}
              readOnly={readOnly}
              className={`block w-full pr-10 sm:text-sm border-secondary-300 rounded-md ${
                readOnly 
                  ? 'bg-gray-50 text-gray-900 cursor-default focus:ring-0 focus:border-secondary-300' 
                  : 'focus:ring-primary-500 focus:border-primary-500'
              } ${errors.password ? 'border-danger-300' : ''}`}
              placeholder={readOnly ? '' : "••••••••"}
            />
            {!readOnly && (
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-secondary-400 hover:text-secondary-500 focus:outline-none"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <EyeIcon className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
            )}
          </div>
          {errors.password && (
            <p className="mt-2 text-sm text-danger-600">{errors.password}</p>
          )}
        </div>

        <div className="sm:col-span-6">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="use_ssl"
                name="useSSL"
                type="checkbox"
                checked={details.useSSL === undefined ? true : details.useSSL}
                onChange={(e) => handleChange({
                  target: {
                    name: 'useSSL',
                    value: e.target.checked
                  }
                })}
                disabled={readOnly}
                className={readOnly ? 'cursor-default opacity-50' : ''}
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="use_ssl" className="font-medium text-secondary-700">
                Use SSL
              </label>
              <p className="text-secondary-500">
                Connect to PostgreSQL using SSL (recommended for security)
              </p>
            </div>
          </div>
        </div>

      </div>

      <div className="pt-5 border-t border-secondary-200">
        <h4 className="text-sm font-medium text-secondary-900">Advanced Options</h4>

        <div className="mt-4 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
          <div className="sm:col-span-3">
            <label htmlFor="connectionTimeout" className="block text-sm font-medium text-secondary-700">
              Connection Timeout (Seconds)
            </label>
            <input
              type="number"
              name="connectionTimeout"
              id="connectionTimeout"
              value={details.connectionTimeout || 10}
              onChange={handleChange}
              readOnly={readOnly}
              className={`mt-1 block w-full shadow-sm sm:text-sm border-secondary-300 rounded-md ${
                readOnly 
                  ? 'bg-gray-50 text-gray-900 cursor-default focus:ring-0 focus:border-secondary-300' 
                  : 'focus:ring-primary-500 focus:border-primary-500'
              }`}
              placeholder={readOnly ? '' : "10"}
              min="1"
            />
          </div>

          <div className="sm:col-span-3">
            <label htmlFor="statementTimeout" className="block text-sm font-medium text-secondary-700">
              Statement Timeout (Seconds)
            </label>
            <input
              type="number"
              name="statementTimeout"
              id="statementTimeout"
              value={details.statementTimeout || 300}
              onChange={handleChange}
              readOnly={readOnly}
              className={`mt-1 block w-full shadow-sm sm:text-sm border-secondary-300 rounded-md ${
                readOnly 
                  ? 'bg-gray-50 text-gray-900 cursor-default focus:ring-0 focus:border-secondary-300' 
                  : 'focus:ring-primary-500 focus:border-primary-500'
              }`}
              placeholder={readOnly ? '' : "300"}
              min="0"
            />
            <p className="mt-2 text-xs text-secondary-500">
              Maximum time in seconds a query can run before timing out
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostgreSQLConnectionForm;